import { LegacyPortalDashboardService } from "@spwn-portal/grpc";
import firebase from "firebase/app";
import useSWR from "swr";

import { useConnectClient } from "../../utility/connectWeb";
import { minBigInt } from "./minBigInt";

import type { ConnectError } from "@bufbuild/connect";
import type { LegacyPortalDashboardProductType } from "@spwn-portal/grpc";

export type EventSalesDetail = {
  productType: LegacyPortalDashboardProductType;
  productName: string;
  price: number;
  quantity: number;
  uniquePurchaseUserCount: number;
};

type GetEventSalesDetailsResponse = {
  eventSalesDetails: EventSalesDetail[];
  bigqueryLastWrittenAt?: bigint;
};

type GetEventSalesDetailsError = ConnectError | { message: string };

const GetEventSalesDetailsKey = "getEventSalesDetails";

export const useGetEventSalesDetails = (
  eventIds: string[],
  fromDate?: bigint,
  toDate?: bigint
) => {
  const auth = firebase.auth();
  const connectClient = useConnectClient(LegacyPortalDashboardService);
  const currentUserId = auth.currentUser?.uid ?? null;

  const {
    data: fetchedData,
    isLoading,
    isValidating,
    error,
    mutate,
  } = useSWR<GetEventSalesDetailsResponse, GetEventSalesDetailsError>(
    currentUserId && eventIds.length > 0
      ? [
          GetEventSalesDetailsKey,
          currentUserId,
          [...eventIds].sort().join(","),
          fromDate?.toString() ?? "",
          toDate?.toString() ?? "",
        ]
      : null,
    async () => {
      if (!connectClient) {
        throw new Error("Connect client is not initialized");
      }
      try {
        const responses = await Promise.all(
          eventIds.map(async (eventId) =>
            connectClient.getEventSalesDetails({
              eventId,
              fromDate,
              toDate,
            })
          )
        );
        const eventSalesDetails = responses.flatMap((response) =>
          response.eventSalesDetails.map((item) => ({
            productType: item.productType,
            productName: item.productName,
            price: item.price,
            quantity: item.quantity,
            uniquePurchaseUserCount: item.uniquePurchaseUserCount,
          }))
        );
        const bigqueryLastWrittenAt = minBigInt(
          responses.map((response) => response.bigqueryLastWrittenAt)
        );
        return {
          eventSalesDetails,
          ...(bigqueryLastWrittenAt !== undefined
            ? { bigqueryLastWrittenAt }
            : {}),
        };
      } catch (error) {
        console.error("Error fetching event sales details:", error);
        throw error;
      }
    }
  );
  return {
    eventIds,
    isLoading,
    isValidating,
    error,
    mutate,
    fetchedData,
  };
};
